// ============================================================
// FILE: JobDetailContent.js
// LOCATION: C:\gardenmarket\web\src\pages\buyer\JobDetailContent.js
// ============================================================
// PURPOSE:
//   Job detail UI for the buyer side. Reused by JobDetail.js
//   (standalone) and BuyerDashboard.js (embedded pane).
//
// LAYOUT CHANGE:
//   Action buttons moved to DetailToolbar at top of pane.
// ============================================================

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import client from '../../api/client';
import ReviewsTable from '../../components/ReviewsTable';
import PhotoUploader from '../../components/PhotoUploader';
import DetailToolbar from '../../components/DetailToolbar';
import ExclusiveStatusCard from '../../components/ExclusiveStatusCard';
import { colors, radius, space, card, cardHeader, statusPill, buyerStatusLabels } from '../../theme/theme';

const TIMELINE_STEPS = [
    { key: 'open',      label: 'Posted' },
    { key: 'quoted',    label: 'Quoted' },
    { key: 'accepted',  label: 'Booked' },
    { key: 'completed', label: 'Completed' },
    { key: 'reviewed',  label: 'Reviewed' },
];

function timelineIndexFor(statusCode) {
    const map = { draft:0, open:0, matched:0, quoted:1, accepted:2, in_progress:2, completed:3, reviewed:4 };
    return map[statusCode] ?? 0;
}

export default function JobDetailContent({ jobId, onJobChanged, embedded = false }) {
    const { user }  = useAuth();
    const navigate  = useNavigate();

    const [job,       setJob]       = useState(null);
    const [quotes,    setQuotes]    = useState([]);
    const [matches,   setMatches]   = useState(null);
    const [reviews,   setReviews]   = useState([]);
    const [loading,   setLoading]   = useState(true);
    const [error,     setError]     = useState('');
    const [accepting, setAccepting] = useState(null);
    const [showWithdrawConfirm, setShowWithdrawConfirm] = useState(false);
    const [withdrawing, setWithdrawing] = useState(false);

    const loadJob = async () => {
        const [j, q, m, r] = await Promise.all([
            client.get(`/jobs/${jobId}`),
            client.get(`/quotes/job/${jobId}`).catch(() => ({ data: { quotes: [] } })),
            client.get(`/jobs/${jobId}/matches`).catch(() => ({ data: { matches: null } })),
            client.get(`/reviews/job/${jobId}`).catch(() => ({ data: { reviews: [] } })),
        ]);
        setJob(j.data.job);
        setQuotes(q.data.quotes || []);
        setMatches(m.data.matches);
        setReviews(r.data.reviews || []);
    };

    useEffect(() => {
        setLoading(true);
        setError('');
        loadJob().catch(err => setError(err.response?.data?.error || 'Failed to load.')).finally(() => setLoading(false));
    }, [jobId]);

    const refreshAndNotify = async () => { await loadJob(); if (onJobChanged) onJobChanged(); };

    const handleAccept = async (quote_id) => {
        setAccepting(quote_id);
        try { await client.post(`/quotes/${quote_id}/accept`); await refreshAndNotify(); }
        catch (err) { setError(err.response?.data?.error || 'Failed to accept quote.'); }
        finally { setAccepting(null); }
    };

    const handleMarkComplete = async () => {
        try { await client.put(`/jobs/${jobId}/status`, { new_status_code: 'completed' }); await refreshAndNotify(); }
        catch (err) { setError(err.response?.data?.error || 'Failed to update status.'); }
    };

    const handleWithdraw = async () => {
        setWithdrawing(true);
        try {
            await client.post(`/jobs/${jobId}/withdraw`, { reason: 'Withdrawn by buyer' });
            if (onJobChanged) onJobChanged(); else navigate('/buyer/dashboard');
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to withdraw.');
            setShowWithdrawConfirm(false);
        } finally { setWithdrawing(false); }
    };

    if (loading) return <div style={styles.loading}>Loading...</div>;
    if (!job)    return <div style={styles.loading}>{error}</div>;

    const acceptedQuote = quotes.find(q => q.status_code === 'accepted');
    const canEdit       = ['draft','open','matched'].includes(job.status_code);
    const canWithdraw   = !['accepted','completed','reviewed','closed'].includes(job.status_code);
    const currentStep   = timelineIndexFor(job.status_code);
    const isClosed      = job.status_code === 'closed';
    const stepsDone     = isClosed ? (quotes.some(q=>q.status_code==='accepted') ? 2 : quotes.length > 0 ? 1 : 0) : currentStep;
    const allPhotos     = job.photos || [];
    const postingPhotos = allPhotos.filter(p => !p.photo_type || p.photo_type === 'job_posting');
    const beforePhotos  = allPhotos.filter(p => p.photo_type === 'before');
    const afterPhotos   = allPhotos.filter(p => p.photo_type === 'after');

    const toolbarActions = [
        canEdit && { label: '✏️ Edit', href: `/buyer/job/${jobId}/edit`, style: 'secondary' },
        canWithdraw && { label: 'Withdraw', onClick: () => setShowWithdrawConfirm(true), style: 'danger' },
        job.status_code === 'accepted' && { label: '✓ Mark as Completed', onClick: handleMarkComplete, style: 'info' },
        job.status_code === 'completed' && !reviews.some(r => r.reviewer_role === 'buyer') &&
            { label: '⭐ Leave a Review', href: `/buyer/job/${jobId}/review`, style: 'primary' },
    ].filter(Boolean);

    return (
        <div style={embedded ? {} : styles.page}>
            {!embedded && <div style={styles.backRow}><Link to="/buyer/dashboard" style={styles.back}>← Back</Link></div>}

            <DetailToolbar actions={toolbarActions} />

            {showWithdrawConfirm && (
                <div style={styles.confirmBox}>
                    <p style={{fontWeight:'bold', marginBottom:'8px'}}>Withdraw this job?</p>
                    <p style={{color:colors.textMuted, fontSize:'0.9rem', marginBottom:'14px'}}>This cannot be undone.</p>
                    <div style={{display:'flex', gap:'8px'}}>
                        <button onClick={handleWithdraw} disabled={withdrawing}
                            style={{padding:'7px 14px', backgroundColor:'#c0392b', color:'white', border:'none', borderRadius:radius.sm, cursor:'pointer', fontWeight:600}}>
                            {withdrawing ? 'Withdrawing...' : 'Yes, withdraw'}
                        </button>
                        <button onClick={() => setShowWithdrawConfirm(false)}
                            style={{padding:'7px 14px', backgroundColor:'white', color:colors.textMuted, border:`1px solid ${colors.border}`, borderRadius:radius.sm, cursor:'pointer'}}>
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {error && <div style={styles.error}>{error}</div>}

            {/* Timeline */}
            <div style={styles.timelineWrap}>
                <div style={styles.timeline}>
                    {TIMELINE_STEPS.map((step, i) => {
                        const done    = i < (isClosed ? stepsDone : currentStep);
                        const current = !isClosed && i === currentStep;
                        const active  = isClosed ? i <= stepsDone : i <= currentStep;
                        return (
                            <div key={step.key} style={styles.timelineStep}>
                                {i < TIMELINE_STEPS.length - 1 && <div style={{...styles.timelineLine, backgroundColor: done ? colors.brand : '#e2e5e8'}} />}
                                <div style={{...styles.timelineDot, backgroundColor: active ? (isClosed ? '#9E9E9E' : colors.brand) : '#e2e5e8', color: active ? 'white' : colors.textFaint}}>
                                    {done ? '✓' : i + 1}
                                </div>
                                <span style={{...styles.timelineLabel, color: active ? colors.text : colors.textFaint, fontWeight: current ? 700 : 500}}>{step.label}</span>
                            </div>
                        );
                    })}
                    {isClosed && (
                        <div style={styles.timelineStep}>
                            <div style={{...styles.timelineDot, backgroundColor:'#c0392b', color:'white'}}>✕</div>
                            <span style={{...styles.timelineLabel, color:'#c0392b', fontWeight:700}}>Withdrawn</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Header */}
            <div style={styles.header}>
                <h2 style={styles.title}>{job.title} <span style={styles.jobIdTag}>#{job.job_id}</span></h2>
                <div style={styles.metaRow}>
                    <span style={statusPill(job.status_code)}>{buyerStatusLabels[job.status_code] || job.status_code}</span>
                    <span style={styles.metaItem}>📍 {job.suburb_name} {job.postcode}</span>
                    {job.preferred_date && <span style={styles.metaItem}>📅 {new Date(job.preferred_date).toLocaleDateString()}</span>}
                    {job.budget && <span style={styles.metaItem}>💰 ${job.budget}</span>}
                </div>
            </div>

            <div style={styles.grid}>
                <div>
                    {/* Directed-job status. Renders nothing for a normal
                        marketplace post. Sits above the description because
                        "who can see this" is the buyer's first question when
                        no quotes have arrived. */}
                    <ExclusiveStatusCard jobId={jobId} role="buyer" onChanged={refreshAndNotify} />

                    <div style={card}><h3 style={cardHeader}>Description</h3><p style={styles.desc}>{job.description}</p></div>

                    {job.skills?.length > 0 && (
                        <div style={card}><h3 style={cardHeader}>Skills Required</h3>
                            <div style={styles.chips}>{job.skills.map(s => <span key={s.skill_id} style={styles.chip}>{s.skill_name}</span>)}</div>
                        </div>
                    )}

                    {canEdit ? (
                        <div style={card}><h3 style={cardHeader}>Job Photos</h3>
                            <PhotoUploader uploadUrl={`/photos/job/${jobId}`} extraFields={{photo_type:'job_posting'}} existingPhotos={postingPhotos} onDeleteUrl={p=>`/photos/job/${p.photo_id}`} onChange={loadJob} label="" maxFiles={9} />
                        </div>
                    ) : postingPhotos.length > 0 && (
                        <div style={card}><h3 style={cardHeader}>Job Photos</h3>
                            <div style={styles.photoGrid}>{postingPhotos.map(p=><img key={p.photo_id} src={p.storage_url} alt="" style={styles.photo} onError={e=>e.target.style.display='none'}/>)}</div>
                        </div>
                    )}

                    {beforePhotos.length > 0 && <div style={card}><h3 style={cardHeader}>Before Photos</h3><div style={styles.photoGrid}>{beforePhotos.map(p=><img key={p.photo_id} src={p.storage_url} alt="" style={styles.photo} onError={e=>e.target.style.display='none'}/>)}</div></div>}
                    {afterPhotos.length > 0  && <div style={card}><h3 style={cardHeader}>After Photos</h3><div style={styles.photoGrid}>{afterPhotos.map(p=><img key={p.photo_id} src={p.storage_url} alt="" style={styles.photo} onError={e=>e.target.style.display='none'}/>)}</div></div>}

                    {acceptedQuote && (
                        <div style={styles.acceptedCard}>
                            <h3 style={styles.cardHeaderGreen}>✅ Booked Gardener</h3>
                            <Link to={`/gardener-profile/${acceptedQuote.gardener_id}`} style={{fontWeight:700, fontSize:'1.05rem', marginBottom:'6px', color:colors.brand, textDecoration:'none', display:'block'}}>
                                👤 {acceptedQuote.gardener_name} →
                            </Link>
                            <div style={{display:'flex', gap:'14px', fontSize:'0.85rem', color:colors.textMuted}}>
                                <span>📅 {acceptedQuote.proposed_date ? new Date(acceptedQuote.proposed_date).toLocaleDateString() : 'TBC'}</span>
                                <span>💰 ${acceptedQuote.quoted_price}</span>
                            </div>
                        </div>
                    )}

                    <ReviewsTable reviews={reviews} currentUserId={user?.user_id} />
                </div>

                <div>
                    {matches?.total_matched > 0 && (
                        <div style={card}><h3 style={cardHeader}>Gardener Activity</h3>
                            <div style={{display:'flex', gap:'12px', flexWrap:'wrap', fontSize:'0.82rem', color:colors.textMuted}}>
                                <span>🔔 {matches.notified_count} notified</span>
                                <span>👁 {matches.viewed_count} viewed</span>
                                <span>💬 {matches.quoted_count} quoted</span>
                                {matches.declined_count > 0 && <span>✗ {matches.declined_count} declined</span>}
                            </div>
                        </div>
                    )}

                    <div style={card}>
                        <h3 style={cardHeader}>Quotes ({quotes.filter(q=>q.status_code!=='declined').length})</h3>
                        {quotes.length === 0 && <p style={{color:colors.textMuted, fontSize:'0.85rem', textAlign:'center', padding:'16px 0'}}>No quotes yet. Gardeners have been notified.</p>}
                        {quotes.map(quote => (
                            <div key={quote.quote_id} style={{border:`1px solid ${quote.status_code==='accepted'?'#a7d8b5':colors.border}`, backgroundColor:quote.status_code==='accepted'?'#f4fbf6':'white', borderRadius:radius.md, padding:space.md, marginBottom:'10px', opacity:quote.status_code==='declined'?0.5:1}}>
                                <div style={{display:'flex', justifyContent:'space-between', marginBottom:'6px'}}>
                                    <div>
                                        <span style={{fontWeight:700, color:colors.text, marginRight:'6px'}}>{quote.gardener_name}</span>
                                        <span style={{fontSize:'0.72rem', backgroundColor:'#fff3cd', padding:'2px 8px', borderRadius:radius.pill, color:'#9a6700'}}>{quote.tier_label}</span>
                                    </div>
                                    <span style={{fontSize:'1.25rem', fontWeight:700, color:colors.brand}}>${quote.quoted_price}</span>
                                </div>
                                <div style={{display:'flex', gap:'10px', fontSize:'0.78rem', color:colors.textMuted, marginBottom:'6px', flexWrap:'wrap'}}>
                                    {quote.avg_rating && <span>⭐ {Number(quote.avg_rating).toFixed(1)}</span>}
                                    <span>✅ {quote.total_jobs} jobs</span>
                                    {quote.estimated_hours && <span>⏱ ~{quote.estimated_hours}hrs</span>}
                                </div>
                                {quote.proposed_date && <p style={{fontSize:'0.8rem', color:colors.textMuted, marginBottom:'5px'}}>📅 {new Date(quote.proposed_date).toLocaleDateString()}</p>}
                                {quote.notes && <p style={{fontSize:'0.84rem', fontStyle:'italic', borderLeft:`3px solid ${colors.brandLight}`, paddingLeft:'8px', margin:'6px 0', color:colors.textMuted}}>{quote.notes}</p>}
                                {quote.status_code === 'submitted' && job.status_code === 'quoted' && (
                                    <button onClick={() => handleAccept(quote.quote_id)} disabled={accepting===quote.quote_id}
                                        style={{width:'100%', padding:'8px', backgroundColor:colors.brand, color:'white', border:'none', borderRadius:radius.sm, cursor:'pointer', fontWeight:600, marginTop:'6px', fontSize:'0.85rem'}}>
                                        {accepting===quote.quote_id ? 'Accepting...' : 'Accept this Quote'}
                                    </button>
                                )}
                                {quote.status_code === 'accepted' && <div style={{textAlign:'center', padding:'7px', backgroundColor:colors.brandLight, color:colors.brand, borderRadius:radius.sm, fontWeight:600, fontSize:'0.85rem', marginTop:'6px'}}>✓ Quote Accepted</div>}
                                {quote.status_code === 'declined' && <div style={{textAlign:'center', padding:'7px', backgroundColor:'#f5f5f5', color:colors.textFaint, borderRadius:radius.sm, fontSize:'0.85rem', marginTop:'6px'}}>Not selected</div>}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

const styles = {
    page:           { maxWidth:'1100px', margin:'0 auto', padding:'32px 24px' },
    loading:        { textAlign:'center', padding:'60px', color:colors.textMuted },
    backRow:        { marginBottom:'16px' },
    back:           { color:colors.brand, fontSize:'0.9rem' },
    confirmBox:     { backgroundColor:'#fff8e1', border:'1px solid #ffe082', borderRadius:radius.md, padding:'16px', marginBottom:'16px' },
    error:          { backgroundColor:'#fdecea', border:'1px solid #f5c2bb', borderRadius:radius.sm, padding:'10px', color:'#c0392b', marginBottom:'14px', fontSize:'0.88rem' },
    timelineWrap:   { marginBottom:space.lg },
    timeline:       { display:'flex', alignItems:'flex-start' },
    timelineStep:   { display:'flex', flexDirection:'column', alignItems:'center', flex:1, position:'relative' },
    timelineDot:    { width:'24px', height:'24px', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.7rem', fontWeight:700, marginBottom:'6px', zIndex:1 },
    timelineLabel:  { fontSize:'0.72rem', textAlign:'center' },
    timelineLine:   { position:'absolute', top:'11px', left:'50%', width:'100%', height:'1.5px', zIndex:0 },
    header:         { marginBottom:space.lg },
    title:          { fontSize:'1.4rem', fontWeight:700, color:colors.text, marginBottom:'10px' },
    jobIdTag:       { fontSize:'0.85rem', color:colors.textFaint, fontWeight:400 },
    metaRow:        { display:'flex', gap:'10px', flexWrap:'wrap', alignItems:'center' },
    metaItem:       { color:colors.textMuted, fontSize:'0.85rem' },
    grid:           { display:'grid', gridTemplateColumns:'1fr 1fr', gap:space.md },
    desc:           { color:colors.textMuted, lineHeight:'1.6', fontSize:'0.92rem' },
    chips:          { display:'flex', flexWrap:'wrap', gap:'8px' },
    chip:           { padding:'5px 13px', borderRadius:radius.pill, backgroundColor:colors.brandLight, color:colors.brand, fontSize:'0.8rem', fontWeight:500 },
    photoGrid:      { display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'8px' },
    photo:          { width:'100%', height:'80px', objectFit:'cover', borderRadius:radius.sm },
    acceptedCard:   { background:'#f4fbf6', border:'1px solid #c7e8d1', borderRadius:radius.lg, padding:space.lg, marginBottom:space.md },
    cardHeaderGreen:{ fontSize:'0.85rem', fontWeight:700, color:colors.brand, marginBottom:'10px' },
};
